import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },

  dragOverlay: {
    background: 'rgba(25, 118, 210, 0.15)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "2.5em",
    border: "5px dashed #1976d2",
    color: '#1976d2',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    pointerEvents: "auto",
    backdropFilter: "blur(2px)",
  },
}));

const shouldPreserveContactTags = (incomingTags, existingTags) =>
  incomingTags === undefined &&
  Array.isArray(existingTags) &&
  existingTags.length > 0;

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);
  const { companyId } = user;

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setContact(data.contact);
            // setWhatsapp(data.whatsapp);
            // setQueueId(data.queueId);
            setTicket(data);
            
            // Verificar se há parâmetro 'tab' na URL - TEM PRIORIDADE ABSOLUTA
            const urlParams = new URLSearchParams(window.location.search);
            const tabParam = urlParams.get('tab');
            
            // Se houver parâmetro tab na URL, usar ele SEMPRE (tem prioridade absoluta)
            // NÃO usar o status do ticket se houver parâmetro na URL
            if (tabParam && ["pending", "open", "group", "closed"].includes(tabParam)) {
              // Usar setTimeout para garantir que seja aplicado depois que o TicketsManagerTabs carregar
              // E usar um delay maior para garantir que o TicketsManagerTabs já processou o parâmetro
              setTimeout(() => {
                setTabOpen(tabParam);
              }, 200);
            } else {
              // Só usar o status do ticket se NÃO houver parâmetro na URL
              // E apenas se o status for válido
              if (["pending", "open", "group"].includes(data.status)) {
                setTimeout(() => {
                  setTabOpen(data.status);
                }, 200);
              }
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history, setTabOpen]);

  useEffect(() => {
    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId && socket && typeof socket.on === 'function') {
      //    const socket = socketManager.GetSocket();

      const onConnectTicket = () => {
        if (socket && typeof socket.emit === 'function') {
          socket.emit("joinChatBox", `${ticket.id}`);
        }
      }

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticket?.id) {
          setTicket((prev) => {
            const incoming = data.ticket;
            const merged = { ...prev, ...incoming };
            if (incoming.contact && prev.contact) {
              merged.contact = { ...prev.contact, ...incoming.contact };
              if (
                shouldPreserveContactTags(incoming.contact?.tags, prev.contact?.tags)
              ) {
                merged.contact.tags = prev.contact.tags;
              }
            } else if (prev.contact && !incoming.contact) {
              merged.contact = prev.contact;
            }
            const hasIncomingTicketTags = Array.isArray(incoming.tags) && incoming.tags.length > 0;
            if (!hasIncomingTicketTags && Array.isArray(prev.tags) && prev.tags.length > 0) {
              merged.tags = prev.tags;
            }
            return merged;
          });
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id !== data.contact?.id) return prevState;
            const merged = { ...prevState, ...data.contact };
            if (
              shouldPreserveContactTags(data.contact?.tags, prevState.tags)
            ) {
              merged.tags = prevState.tags;
            }
            return merged;
          });
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {
        if (socket && typeof socket.emit === 'function') {
          socket.emit("joinChatBoxLeave", `${ticket.id}`);
        }
        if (socket && typeof socket.off === 'function') {
          socket.off("connect", onConnectTicket);
          socket.off(`company-${companyId}-ticket`, onCompanyTicket);
          socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
        }
      };
    }
  }, [ticketId, ticket, history, socket, user.companyId, companyId]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleQuickMessageSelect = (quickMessage) => {
    try {
      if (quickMessage.message) {
        // Disparar evento que o MessageInput vai escutar
        const event = new CustomEvent('insertQuickMessage', {
          detail: { message: quickMessage.message }
        });
        window.dispatchEvent(event);
        
      }
      
      if (quickMessage.mediaPath) {
        // Tratar mídia se necessário
      }
    } catch (error) {
      console.error("Erro ao inserir resposta rápida:", error);
      toastError("Erro ao inserir resposta rápida");
    }
  };

  // Handlers de drag and drop para toda a área do ticket
  const handleTicketDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      const hasFiles = event.dataTransfer &&
        event.dataTransfer.types &&
        (event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('application/x-moz-file'));

      if (hasFiles && !dragActive) {
        // Mostrar overlay imediatamente sem timeout para melhor responsividade
        setDragActive(true);
      }
    } else if (event.type === "dragleave") {
      // Limpar timeout se existir
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      // Só desativar se realmente saiu da área
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  };

  const handleTicketDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Limpar timeout se existir
    if (dragTimeout) {
      clearTimeout(dragTimeout);
      setDragTimeout(null);
    }

    setDragActive(false);

    // Processar arquivos imediatamente sem delays
    if (event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files.length > 0 &&
      event.dataTransfer.files[0] instanceof File) {
      // Converter para array e processar imediatamente
      const filesArray = Array.from(event.dataTransfer.files);
      setDragDropFiles(filesArray);
    }
  };

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          ticketStatus={ticket.status}
          ticketDbId={ticket.id}
          contactId={ticket.contactId || (ticket.contact && ticket.contact.id)}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          /* ⚠️ NÃO REMOVER (2026-08-31): o autocomplete de "@" depende disto.
             Antes o gate usava ticketStatus === "group", que NUNCA acontece quando a
             conexão está com groupAsTicket=enabled (o ticket de grupo nasce "pending"). */
          isGroup={ticket.isGroup}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          contactId={contact.id}
          whatsappId={ticket.whatsappId}
          ticketUserId={ticket.userId}
          ticketQueueId={ticket.queueId}
          onFilesProcessed={() => setDragDropFiles([])}
        />
      </>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen && !isMobile,
        })}
        onDragEnter={handleTicketDrag}
        onDragOver={handleTicketDrag}
        onDragLeave={handleTicketDrag}
        onDrop={handleTicketDrop}
        style={{ position: "relative" }}
      >
        {dragActive && (
          <div 
            className={classes.dragOverlay}
            onDragEnter={handleTicketDrag}
            onDragLeave={handleTicketDrag}
            onDragOver={handleTicketDrag}
            onDrop={handleTicketDrop}
          >
            Solte os arquivos aqui para enviar
          </div>
        )}
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
                // Quando o drawer de contato está aberto no desktop, reduzimos o header
                // para preservar todos os ícones de ação (incluindo o menu de 3 pontos).
                compactDesktop={drawerOpen && !isMobile}
              />
            </div>
          )}
        <TicketActionButtons
          ticket={ticket}
          contact={contact}
          onQuickMessageSelect={handleQuickMessageSelect}
        />
        </TicketHeader>
        {/* </div> */}
        <Paper style={{ 
          padding: 0, 
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: '44px'
        }}>
          <TagsContainer contact={contact} />
        </Paper>
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderMessagesList()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

    </div>
  );
};

export default Ticket;